// The 310px receipt paper, in millimetres at the CSS 96dpi reference.
const PAPER_WIDTH_MM = 82;
const PT_PER_MM = 72 / 25.4;

// PDF syntax is byte-oriented; the string parts are all ASCII, so charCode is the byte.
const asBytes = (text: string): Uint8Array => {
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    bytes[index] = text.charCodeAt(index) & 0xff;
  }
  return bytes;
};

export const dataUrlToBytes = (dataUrl: string): Uint8Array => {
  const binary = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

// Summary: Wraps a baseline JPEG in a one-page PDF sized to the receipt paper. Browsers cannot be
// relied on to print (iOS makes window.print a no-op in standalone mode), so the file is built here.
export const buildJpegPdf = (jpeg: Uint8Array, pixelWidth: number, pixelHeight: number): Uint8Array => {
  const widthPt = Math.round(PAPER_WIDTH_MM * PT_PER_MM * 100) / 100;
  const heightPt = Math.round(widthPt * (pixelHeight / pixelWidth) * 100) / 100;
  const content = `q\n${widthPt} 0 0 ${heightPt} 0 0 cm\n/Im0 Do\nQ\n`;

  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (part: Uint8Array | string) => {
    const bytes = typeof part === 'string' ? asBytes(part) : part;
    chunks.push(bytes);
    length += bytes.length;
  };
  const startObject = (id: number) => {
    offsets[id] = length;
    push(`${id} 0 obj\n`);
  };

  push('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
  startObject(1);
  push('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  startObject(2);
  push('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');
  startObject(3);
  push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${widthPt} ${heightPt}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`);
  startObject(4);
  push(`<< /Type /XObject /Subtype /Image /Width ${pixelWidth} /Height ${pixelHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
  push(jpeg);
  push('\nendstream\nendobj\n');
  startObject(5);
  push(`<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`);

  const xrefOffset = length;
  push('xref\n0 6\n');
  // Every entry is exactly 20 bytes wide, or the table is unreadable.
  push('0000000000 65535 f \n');
  for (let id = 1; id <= 5; id += 1) {
    push(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`);
  }
  push(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

  const pdf = new Uint8Array(length);
  let cursor = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, cursor);
    cursor += chunk.length;
  }
  return pdf;
};

export const downloadPdfFile = (pdf: Uint8Array, filename: string): void => {
  // Copied out of the view so the Blob gets a plain ArrayBuffer, never a SharedArrayBuffer.
  const buffer = new ArrayBuffer(pdf.byteLength);
  new Uint8Array(buffer).set(pdf);
  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// The canvas size is not carried by the data URL, so it is read back off the JPEG frame header.
export const readJpegSize = (jpeg: Uint8Array): { width: number; height: number } | null => {
  let offset = 2;
  while (offset + 9 < jpeg.length) {
    if (jpeg[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = jpeg[offset + 1];
    // SOF0..SOF15 carry the frame dimensions; DHT/DAC/DRI in that range do not.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return {
        height: (jpeg[offset + 5] << 8) | jpeg[offset + 6],
        width: (jpeg[offset + 7] << 8) | jpeg[offset + 8],
      };
    }
    offset += 2 + ((jpeg[offset + 2] << 8) | jpeg[offset + 3]);
  }
  return null;
};

const receiptPdf = (jpegDataUrl: string): Uint8Array | null => {
  const image = dataUrlToBytes(jpegDataUrl);
  const size = readJpegSize(image);
  return size ? buildJpegPdf(image, size.width, size.height) : null;
};

export const receiptPdfBase64 = (jpegDataUrl: string): string => {
  const pdf = receiptPdf(jpegDataUrl);
  if (!pdf) throw new Error('Invalid receipt image');
  const chunks: string[] = [];
  for (let index = 0; index < pdf.length; index += 8192) {
    chunks.push(String.fromCharCode(...pdf.subarray(index, index + 8192)));
  }
  return btoa(chunks.join(''));
};

export const downloadSummaryReceiptPdf = (jpegDataUrl: string, title: string): boolean => {
  const pdf = receiptPdf(jpegDataUrl);
  if (!pdf) {
    return false;
  }
  downloadPdfFile(pdf, `${title}.pdf`);
  return true;
};
